import React, { useEffect, useState } from "react";
import { ArrowRight16Regular, Archive24Regular } from "@fluentui/react-icons";
import { apiFetch } from "../utils/apiFetch";

/** One overwritten survivor field captured at merge time (alt → neu). */
type FieldArchiveDto = {
  id: number;
  column: string;
  oldValue: string | null;
  newValue: string | null;
  sourceContactId: number | null;
  sourceName: string | null;
  mergedByName?: string | null;
  mergedAt?: string | null;
};

/** German labels for the gap-fillable Contact columns (else the raw column).
 *  Exportiert, damit das einheitliche „Archiv" (ContactArchiv) dieselbe
 *  Spalten→Label-Tabelle für Feld-Archiv UND Audit-Log nutzen kann. */
export const FIELD_LABELS: Record<string, string> = {
  contactNbrExt: "Externe Nr.",
  lastname: "Nachname / Firmenname",
  firstName: "Vorname",
  suffix: "Namenszusatz",
  maritalStatus: "Familienstand",
  taxNumber: "Steuernummer",
  remark: "Bemerkung",
  birthDate: "Geburtsdatum",
  birthPlace: "Geburtsort",
  birthName: "Geburtsname",
  nationality: "Staatsangehörigkeit",
  jobStatus: "Beruf",
  industry: "Branche",
  occupation: "Tätigkeit",
  employer: "Arbeitgeber",
  contactValue: "Kundenwert",
  contactRelevance: "Kundenrelevanz",
  phase: "Phase",
  letztesJahresgespraech: "Letztes Jahresgespräch",
  empfehlungsgeber: "Empfehlungsgeber",
  erreichbarkeit: "Erreichbarkeit",
  photo_url: "Foto / Logo",
  financialYearEnd: "Geschäftsjahresende",
  taxDeductible: "Vorsteuerabzug",
  memberships: "Mitgliedschaften",
  tags: "Schlagworte",
  paymentMethodID: "Zahlungsart",
  accountManagerBoID: "Backoffice (alt)",
  accountManagerFoID: "Frontoffice (alt)",
  contactStatusId: "Status",
  zusatzstatus: "Zusatzstatus",
  legalFormId: "Rechtsform",
  oldLegalFormId: "Rechtsform (alt)",
  contactGroupId: "Kundengruppe",
  gruendungsjahr: "Gründungsjahr",
  gruender: "Gründer",
  rechtsform: "Rechtsform (Text)",
  handelsregisterNummer: "Handelsregister-Nr.",
  register: "Register",
  umsatzsteuernummer: "USt-IdNr.",
  finanzamt: "Finanzamt",
  vorsteuerabzugsberechtigt: "Vorsteuerabzugsberechtigt",
  geschaeftsjahrIstKalenderjahr: "Geschäftsjahr = Kalenderjahr",
  steuernummer: "Steuernummer",
  bilanzierungspflichtig: "Bilanzierungspflicht",
  pruefungspflichtHgb: "Prüfungspflicht (HGB)",
  geschaeftsjahrAbweichend: "Abweichendes Geschäftsjahr",
  qualitaetsmanagementVorhanden: "Qualitätsmanagement",
};

const labelFor = (col: string) => FIELD_LABELS[col] ?? col;

const fmt = (raw?: string | null): string => {
  if (!raw) return "";
  const d = new Date(raw);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const showVal = (v: string | null): string =>
  v == null || v === "" ? "—" : v;

type Props = { contactId: number };

const MergeFieldArchive = ({ contactId }: Props) => {
  const [rows, setRows] = useState<FieldArchiveDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    if (!(Number.isFinite(contactId) && contactId > 0)) {
      setRows([]);
      setLoading(false);
      return;
    }
    apiFetch<FieldArchiveDto[]>(
      `duplicate-candidates/${contactId}/field-archive`,
    )
      .then((r) => {
        if (!cancelled) setRows(r.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  return (
    <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "#4d2051",
          fontWeight: 700,
          fontSize: 14,
        }}
      >
        <Archive24Regular />
        Kundentagebuch Archiv
      </div>

      {loading ? (
        <div style={{ color: "#757575", fontSize: 13 }}>Archiv wird geladen…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "#757575", fontSize: 13 }}>
          Für diesen Kontakt sind keine archivierten Änderungen vorhanden.
        </div>
      ) : (
        rows.map((r) => (
          <div
            key={r.id}
            style={{
              display: "grid",
              gridTemplateColumns: "200px minmax(0, 1fr)",
              gap: 12,
              alignItems: "start",
              backgroundColor: "#fff",
              border: "1px solid #ececec",
              borderRadius: 8,
              padding: "12px 14px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#2d142b" }}>
                {labelFor(r.column)}
              </span>
              {r.mergedAt && (
                <span style={{ fontSize: 11, color: "#757575" }}>{fmt(r.mergedAt)}</span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    color: "#8b1c1c",
                    textDecorationLine: "line-through",
                    overflowWrap: "anywhere",
                  }}
                >
                  {showVal(r.oldValue)}
                </span>
                <span style={{ color: "#757575", display: "inline-flex" }}>
                  <ArrowRight16Regular />
                </span>
                <span style={{ color: "#1b5e20", fontWeight: 600, overflowWrap: "anywhere" }}>
                  {showVal(r.newValue)}
                </span>
              </div>
              <span style={{ fontSize: 12, color: "#5b5560" }}>
                {r.sourceContactId != null
                  ? r.sourceName
                    ? `Übernommen aus „${r.sourceName}"`
                    : "Übernommen aus zusammengeführtem Kontakt"
                  : (r.sourceName ?? "Automatische Änderung")}
                {r.mergedByName ? ` · durch ${r.mergedByName}` : ""}
              </span>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default MergeFieldArchive;
